import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { RegisterUserLocation } from '../interfaces/register-user.response';
import { CategorySelection } from '../interfaces/register-user-form.interface';
import { FormGroup } from '@angular/forms';
import { closeSnackbarContainer, getOrCreateSnackbarContainer, openCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { DEFAULT_TIME } from '../shared/components/time-fields/components/time-field/config/constants';
import { TranslationService } from 'src/app/services/translation.service';

@Injectable({
  providedIn: 'root'
})
export class RegisterUserFormService {
  private _translationService = inject(TranslationService);
  // Injected services

  // Signals state
  private _startDate: WritableSignal<Date> = signal(null);
  public startDate = this._startDate.asReadonly();
  
  private _endDate: WritableSignal<Date> = signal<Date>(null);
  public endDate = this._endDate.asReadonly();
  
  private _startTime: WritableSignal<string> = signal<string>(DEFAULT_TIME);
  public startTime = this._startTime.asReadonly();
  
  private _startLocation: WritableSignal<RegisterUserLocation> = signal<RegisterUserLocation>(null);
  public startLocation = this._startLocation.asReadonly();
  
  private _tripTypeId: WritableSignal<number> = signal<number>(null);
  public tripTypeId = this._tripTypeId.asReadonly();

  private _categories: WritableSignal<CategorySelection[]> = signal<CategorySelection[]>([]);
  public categories = this._categories.asReadonly();
  
  private _shouldSubmitForm = signal(false);
  public shouldSubmitForm = this._shouldSubmitForm.asReadonly();
  
  private _activeValidationStep = signal<number | null>(null);
  public activeValidationStep = this._activeValidationStep.asReadonly();
  
  // Setters
  public setStartDate(date: Date): void {
    this._startDate.set(date);
  }
  
  public setEndDate(date: Date): void {
    this._endDate.set(date);
  }
  
  public setStartTime(time: string): void {
    this._startTime.set(time);
  }
  
  public setStartLocation(location: RegisterUserLocation): void {
    this._startLocation.set(location);
  }
  
  public setTripType(tripTypeId: number): void {
    this._tripTypeId.set(tripTypeId);
  }

  public setCategories(categories: CategorySelection[]): void {
    this._categories.set(categories);
  }
  
  public setShouldSubmitForm(value: boolean): void {
    this._shouldSubmitForm.set(value);
  }
  
  public setActiveValidationStep(step: number | null): void {
    this._activeValidationStep.set(step);
  }
  
  // Methods
   /**
   * Returns the tooltip text for the submit button
   * @param form - The FormGroup to check
   */
   getTooltipText(form: FormGroup): string {
    return form.valid
      ? this._translationService.instant('Comenzar a planificar')
      : this._translationService.instant('Revisa los campos obligatorios antes de enviar');
  }

  /**
   * Handles the form submission logic
   * @param form - The FormGroup to submit
   * @param submitFormEmitter - The output emitter for form submission
   */
  /**
   * Triggers the submitRequested signal. The component should observe this signal and emit the output.
   */
  handleSubmit(form: FormGroup): void {
    if (form.invalid) {
      this.setActiveValidationStep(1);
      this.closeErrorFormSnackbar(); // close the previous one if it exists
      this.openErrorFormSnackbar();
    } else {
      this.closeErrorFormSnackbar();
      this._shouldSubmitForm.set(true);
    }
  }

  /**
   * Opens an error snackbar for invalid form submission
   */
  openErrorFormSnackbar(): void {
    openCiSnackbar({
      content: this._translationService.instant('Revisa los campos del formulario que son obligatorios'),
      status: 'error',
      'icon-name': 'error',
      'horizontal-position': 'end',
      'vertical-position': 'bottom',
    });
  }
  
  /**
   * Closes the error snackbar
   */
  closeErrorFormSnackbar(): void {
    const snackbarContainer = getOrCreateSnackbarContainer({
      horizontal: 'end',
      vertical: 'bottom',
    });
    
    if (!snackbarContainer) return;
    
    closeSnackbarContainer(snackbarContainer);
  } 
  
  /**
   * Resets the form to its initial state
   * @param form - The form group to reset
   */
  public resetForm(form: FormGroup): void {
    // Reset all signals to their initial values
    this._startDate.set(null);
    this._endDate.set(null);
    this._startTime.set(DEFAULT_TIME);
    this._startLocation.set(null);
    this._tripTypeId.set(null);
    this._categories.set([]);

    // Reset the form with the initial values
    form.reset({
      startDate: null,
      endDate: null,
      startTime: DEFAULT_TIME,
      startLocationAddress: null,
      startLocationLat: null,
      startLocationLng: null,
      tripType: null,
      categories: [],
      subdomain: form.get('subdomain')?.value
    });
  }
}
